import { cn } from '@/utilities/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { formatSlug } from '@/fields/slug/formatSlug'
import React from 'react'

const variants = cva(
  ['font-subheading', 'font-weight-subheading', 'leading-subheading', 'tracking-subheading'],
  {
    defaultVariants: {
      size: 'lg',
    },
    variants: {
      size: {
        lg: 'text-subheading-lg',
        md: 'text-subheading-md',
        sm: 'text-subheading-sm',
        xs: 'text-subheading-xs',
      },
    },
  },
)

type SubheadingProps = React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof variants> & {
    anchorLink?: boolean
    showAnchor?: boolean
    id?: string
  }

export const Subheading = React.forwardRef<HTMLHeadingElement, SubheadingProps>(
  ({ size, className, id, children, anchorLink, showAnchor, ...props }, ref) => {
    const el = (
      <h5 {...props} ref={ref} className={cn(variants({ size, className }))}>
        {children}
      </h5>
    )
    const cId = id || (typeof children === 'string' ? formatSlug(children.toString()) : '')

    if (anchorLink) {
      return (
        <div className={cn('group relative flex flex-col gap-2 items-start', className)}>
          <div id={cId} className="absolute -top-[100px]" />
          <a href={`#${cId}`} className="relative hover:underline">
            {showAnchor && (
              <span
                className={cn(
                  'absolute -left-[30px] hidden group-hover:inline-block',
                  variants({ size }),
                  className,
                )}
              >
                #
              </span>
            )}
            {el}
          </a>
        </div>
      )
    }

    return el
  },
)

Subheading.displayName = 'Subheading'
